/**
 * P2P Page Layout Component
 * Provides consistent layout wrapper for P2P pages
 */

import React, { PropsWithChildren, memo } from "react";
import BreadCrumbs, {
  BreadcrumbItem,
} from "../../../components/common/BreadCrumbs";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";

interface P2PPageLayoutProps {
  /** Optional breadcrumb items (new format - supports multiple levels) */
  breadcrumbItems?: BreadcrumbItem[];
  /** Optional breadcrumb previous page text (legacy - for backward compatibility) */
  previousPage?: string;
  /** Optional breadcrumb click handler (legacy - for backward compatibility) */
  onPreviousClick?: () => void;
  /** Optional page title */
  title?: string;
  /** Optional navigation links (e.g., Projects, Members, etc.) */
  navigationLinks?: React.ReactNode;
  /** Whether to show scroll to top button (default: true) */
  showScrollToTop?: boolean;
}

/**
 * Layout wrapper for P2P pages with consistent structure
 * Includes breadcrumbs, optional title, optional team name subtitle, and scroll to top button
 *
 * @example
 * ```tsx
 * // Using new breadcrumbItems format with team name
 * <P2PPageLayout
 *   breadcrumbItems={[
 *     { label: "UBT Home", onClick: () => navigate(-1) },
 *     { label: "Team Alpha", path: `/home/projects/${teamId}` },
 *     { label: "Path To Performance" }
 *   ]}
 *   title="Path to Performance"
 *   navigationLinks={<P2PNavigationLinks teamId={123} />}
 * >
 *   {children}
 * </P2PPageLayout>
 *
 * // Legacy format (backward compatible)
 * <P2PPageLayout
 *   previousPage="UBT Home"
 *   onPreviousClick={() => navigate(-1)}
 *   title="Path to Performance"
 * >
 *   {children}
 * </P2PPageLayout>
 * ```
 */
const P2PPageLayout: React.FC<PropsWithChildren<P2PPageLayoutProps>> = memo(
  ({
    breadcrumbItems,
    previousPage = "UBT Home",
    onPreviousClick,
    title,
    navigationLinks,
    showScrollToTop = true,
    children,
  }) => {
    return (
      <div className="min-h-[70vh] lg:mx-40! mx-5!">
        {/* Breadcrumbs and Navigation */}
        <div className="flex justify-between items-end mb-6">
          <div className="mb-6!">
            <BreadCrumbs
              items={breadcrumbItems}
              previousPage={previousPage}
              onPreviousClick={onPreviousClick}
            />
          </div>
          {navigationLinks}
        </div>

        {/* Optional Title */}
        {title && (
          <div className="flex justify-between items-end mb-4! md:mb-0! margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
            <div className="w-full">
              <h1 className="h1 desktop:mb-6!  tablet:mt-8! mobile:mt-3.5!">{title}</h1>
            </div>
          </div>
        )}
        
        {/* Main Content */}
        {children}

        {/* Scroll to Top Button */}
        {showScrollToTop && (
          <div className="mb-10!">
            <div className="-mb-27! tablet:hidden">
              <ScrollToTopBtn />
            </div>
          </div>
        )}
      </div>
    );
  },
);

P2PPageLayout.displayName = "P2PPageLayout";

export default P2PPageLayout;
